import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, X, Loader2, Copy, Check } from "lucide-react";
import { initials } from "../lib/constants";

export function copyText(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
  return Promise.resolve();
}

// Click-to-copy text: the value itself is the button, with a small icon
// that appears on hover and flips to a check for a moment after copying.
export function Copyable({ value, children, className = "", testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = (e) => {
    e.stopPropagation();
    copyText(value)
      .then(() => {
        setCopied(true);
        toast("Copied");
        setTimeout(() => setCopied(false), 1400);
      })
      .catch(() => {});
  };
  return (
    <button type="button" onClick={copy} title="Copy to clipboard" data-testid={testid}
      className={`group inline-flex items-center gap-1.5 ${className}`}>
      <span>{children ?? value}</span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
      )}
    </button>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-800">
      {tabs.map((t) => (
        <button
          key={t.value}
          onClick={() => onChange(t.value)}
          data-testid={`tab-${t.value}`}
          className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === t.value
              ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
              : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          }`}
        >
          {t.icon && <t.icon className="w-4 h-4" />} {t.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin ${className}`} />;
}

export function PageLoader() {
  return (
    <div className="flex h-72 items-center justify-center text-slate-400" data-testid="page-loader">
      <Spinner className="w-6 h-6" />
    </div>
  );
}

export function Avatar({ name, size = "h-9 w-9", className = "" }) {
  return (
    <div className={`${size} ${className} flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300 text-xs font-semibold shrink-0`}>
      {initials(name)}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "text-indigo-600 dark:text-indigo-400", active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`card p-4 text-left transition-all hover:shadow-md ${active ? "ring-2 ring-indigo-500 border-indigo-300 dark:border-indigo-700" : ""} ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
        {Icon && <Icon className={`w-5 h-5 ${accent}`} strokeWidth={1.8} />}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-white font-display">{value}</div>
    </button>
  );
}

export function Badge({ children, className = "" }) {
  return <span className={`badge ${className}`}>{children}</span>;
}

// Status dropdown rendered as a badge that opens a menu
export function StatusDropdown({ type, value, options, onChange, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = options.find((o) => o.value === value) || { label: value || "—", cls: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700", dot: "bg-slate-400" };
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        data-testid={testid}
        className={`badge ${current.cls} ${disabled ? "" : "hover:opacity-80 cursor-pointer"}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${current.dot}`} />
        {current.label}
        {!disabled && <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-44 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#111827] shadow-lg animate-zoom-in py-1 max-h-64 overflow-auto">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(o.value); setOpen(false); }}
              data-testid={`${testid}-opt-${o.value}`}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${o.dot}`} />
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Select({ value, onChange, options, placeholder, className = "", testid }) {
  return (
    <select
      className={`input ${className}`}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function Modal({ open, onClose, title, children, footer, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md card p-6 animate-zoom-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold font-display text-slate-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" data-testid="modal-close" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
      <span className="text-slate-500 dark:text-slate-400">
        Showing <b className="text-slate-700 dark:text-slate-200">{start.toLocaleString()}-{end.toLocaleString()}</b> of <b className="text-slate-700 dark:text-slate-200">{total.toLocaleString()}</b>
      </span>
      <div className="flex items-center gap-1">
        <button className="btn-secondary px-3 py-1.5" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev">Prev</button>
        <span className="px-3 text-slate-600 dark:text-slate-300">{page} / {pages}</span>
        <button className="btn-secondary px-3 py-1.5" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="page-next">Next</button>
      </div>
    </div>
  );
}

// Simple toast system
let toastId = 0;
const listeners = new Set();
export function toast(message, type = "success") {
  const t = { id: ++toastId, message, type };
  listeners.forEach((l) => l(t));
}
export function Toaster() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const l = (t) => {
      setItems((prev) => [...prev, t]);
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), 3200);
    };
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  return (
    <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2" data-testid="toaster">
      {items.map((t) => (
        <div key={t.id} className={`card px-4 py-3 text-sm shadow-lg animate-slide-up min-w-[220px] ${t.type === "error" ? "border-rose-300 dark:border-rose-800" : "border-emerald-300 dark:border-emerald-800"}`}>
          <span className={t.type === "error" ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

/* Recharts' built-in tooltip paints itself white with light grey text, which is
   fine on the light theme and unreadable on the dark one - a white bubble with
   near-white writing in it. Styling it through contentStyle would mean picking
   colours in JavaScript and tracking the theme by hand; giving it a body of our
   own lets the same Tailwind dark: classes the rest of the app uses do the work.
   Recharts clones this element with { active, payload, label }, so any prop set
   at the call site - a formatter, say - survives. */
export function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 shadow-lg">
      {label !== undefined && label !== null && label !== "" && (
        <div className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-200">{label}</div>
      )}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs leading-5">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: p.color || p.fill || "#6366f1" }} />
          <span className="text-slate-500 dark:text-slate-400">{p.name}</span>
          <span className="ml-auto font-semibold text-slate-900 dark:text-white">
            {formatter ? formatter(p.value, p.name, p) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}
